// Phase 1C FINAL — aggregate viewer-map status from actual TileRecords.
//
// A genuinely loaded empty tile (hasSnapshot=true, parcels=[]) is VALID data.
// The aggregate MUST NOT use parcels.length; it uses hasSnapshot/state/refresh
// error semantics as maintained by tile-reducer.
import type { TileRecord } from "./types";

export type MapStatusKind =
  | "initial-loading"
  | "partial-loading"
  | "ready"
  | "refreshing-stale"
  | "error-no-snapshot"
  | "error-with-snapshot";

export interface MapStatus {
  kind: MapStatusKind;
  message: string;
}

export function deriveMapStatus(tiles: TileRecord[]): MapStatus {
  const total = tiles.length;
  let withSnap = 0;
  let loading = 0;
  let missing = 0;
  let hardErrors = 0;
  let staleRefreshing = 0;
  let refreshFailed = 0;

  for (const t of tiles) {
    if (t.hasSnapshot) withSnap++;
    if (t.state === "loading") loading++;
    else if (t.state === "missing") missing++;
    else if (t.state === "error") hardErrors++;
    else if (t.state === "stale") staleRefreshing++;
    if (t.refreshError != null) refreshFailed++;
  }

  // No snapshot at all
  if (withSnap === 0) {
    if (hardErrors > 0) {
      return { kind: "error-no-snapshot", message: "Map data unavailable" };
    }
    // still loading OR missing not yet requested
    return { kind: "initial-loading", message: "Loading map data…" };
  }

  // Some snapshots + others still being fetched
  if (withSnap < total) {
    if (loading > 0 || missing > 0) {
      return {
        kind: "partial-loading",
        message: "Map partially loaded — loading remaining regions…",
      };
    }
    if (hardErrors > 0) {
      return {
        kind: "error-with-snapshot",
        message: "Map partially unavailable — some regions failed to load",
      };
    }
  }

  // All tiles have snapshots — evaluate refresh state
  if (refreshFailed > 0) {
    return {
      kind: "error-with-snapshot",
      message: "Map refresh failed — showing last known snapshot",
    };
  }
  if (staleRefreshing > 0) {
    return { kind: "refreshing-stale", message: "Updating map data…" };
  }
  return { kind: "ready", message: "" };
}
